import { toast } from '@ifixit/ui/components/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { apiClient } from 'Shared/api_client';

export const STEP_RAIL_MODES = ['list', 'grid'] as const;

export type StepRailMode = (typeof STEP_RAIL_MODES)[number];

export function isStepRailMode(value: string): value is StepRailMode {
   return (STEP_RAIL_MODES as readonly string[]).includes(value);
}

/** One field per case of EditorPreference.php; the page prop and the endpoint share it. */
export type EditorPreferences = {
   stepRailMode: StepRailMode;
};

/**
 * Seeded from the page's props, so the first paint is right. A change shows at once; when the
 * last queued save fails, the screen goes back to the last saved value, and each failure toasts.
 */
export function useEditorPreference<Key extends keyof EditorPreferences>(
   key: Key,
   initial: EditorPreferences[Key],
   failureMessage: string
): readonly [EditorPreferences[Key], (next: EditorPreferences[Key]) => void] {
   const queryClient = useQueryClient();
   const [value, setValue] = useState(initial);
   const shownRef = useRef(initial);
   const savedRef = useRef(initial);
   const mutationKey = ['editor-preference', key];

   const save = useMutation({
      mutationKey,
      // Saves for one key run one at a time, in the order they were made, so
      // the server ends on the last change that succeeded.
      scope: { id: `editor-preference-${key}` },
      mutationFn: async (next: EditorPreferences[Key]) => {
         await apiClient.patch('user/editor-preferences', { [key]: next });
      },
      onSuccess: (_result, next) => {
         savedRef.current = next;
      },
      onError: () => {
         // This save still counts as running here. Any other is queued behind
         // it and decides what the server ends on.
         if (queryClient.isMutating({ mutationKey }) === 1) {
            shownRef.current = savedRef.current;
            setValue(savedRef.current);
         }
         toast.error(failureMessage, { duration: 5000 });
      },
   });

   const change = (next: EditorPreferences[Key]) => {
      if (next === shownRef.current) {
         return;
      }
      shownRef.current = next;
      setValue(next);
      save.mutate(next);
   };

   return [value, change];
}
